import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, adminHeaders, clearAdminToken, getAdminToken, PROPERTY_NAMES, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { format, addDays, subDays } from "date-fns";
import { it } from "date-fns/locale";
import {
  ChevronLeft,
  ChevronRight,
  LogOut,
  BedDouble,
  CalendarClock,
  Sun,
  MoonStar,
  Loader2,
  Trash2,
  Download,
  Save,
  Tag,
  QrCode,
  Search,
  X,
} from "lucide-react";
import { BookingSuccessDialog } from "@/components/BookingSuccessDialog";
import { RevenueTab } from "@/components/RevenueTab";
import { HistoryTab } from "@/components/HistoryTab";

const DAYS_WINDOW = 14;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(new Date(new Date().toDateString()));
  const [statusData, setStatusData] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState({ total: 0, upcoming: 0, dayuse: 0, overnight: 0 });
  const [pricing, setPricing] = useState(null);
  const [pricingDraft, setPricingDraft] = useState(null);
  const [savingPricing, setSavingPricing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("calendar");
  const [qrBooking, setQrBooking] = useState(null);
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!getAdminToken()) {
      navigate("/admin/login");
    }
  }, [navigate]);

  const endDate = useMemo(() => addDays(startDate, DAYS_WINDOW - 1), [startDate]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [statusRes, bookingsRes, statsRes, pricingRes] = await Promise.all([
        api.get("/admin/room-status", {
          params: {
            start: format(startDate, "yyyy-MM-dd"),
            end: format(endDate, "yyyy-MM-dd"),
          },
          ...adminHeaders(),
        }),
        api.get("/admin/bookings", { ...adminHeaders(), params: { upcoming: true } }),
        api.get("/admin/stats", { ...adminHeaders() }),
        api.get("/pricing"),
      ]);
      setStatusData(statusRes.data);
      setBookings(bookingsRes.data);
      setStats(statsRes.data);
      setPricing(pricingRes.data);
      setPricingDraft(pricingRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        clearAdminToken();
        navigate("/admin/login");
      } else {
        toast.error("Errore nel caricamento");
      }
    } finally {
      setLoading(false);
    }
  };

  const savePricing = async () => {
    if (!pricingDraft) return;
    setSavingPricing(true);
    try {
      const payload = {
        dayuse_standard_3h: parseFloat(pricingDraft.dayuse_standard_3h),
        dayuse_vasca_2h: parseFloat(pricingDraft.dayuse_vasca_2h),
        overnight_standard: parseFloat(pricingDraft.overnight_standard),
        overnight_suite: parseFloat(pricingDraft.overnight_suite),
        overnight_bnb: parseFloat(pricingDraft.overnight_bnb),
        supplement_per_extra_guest: parseFloat(pricingDraft.supplement_per_extra_guest),
        weekend_surcharge_per_night: parseFloat(pricingDraft.weekend_surcharge_per_night),
      };
      const res = await api.put("/admin/pricing", payload, adminHeaders());
      setPricing(res.data);
      setPricingDraft(res.data);
      toast.success("Prezzi aggiornati");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore salvataggio prezzi");
    } finally {
      setSavingPricing(false);
    }
  };

  const exportCSV = async () => {
    try {
      const res = await fetch(`${API}/admin/bookings/export`, {
        headers: { Authorization: `Bearer ${getAdminToken()}` },
      });
      if (!res.ok) throw new Error("HTTP " + res.status);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `prenotazioni_${format(new Date(), "yyyyMMdd_HHmm")}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV scaricato");
    } catch (err) {
      toast.error("Errore export CSV");
    }
  };

  // Debounced search across all bookings by ref / name / phone
  useEffect(() => {
    if (!search.trim()) {
      setSearchResults(null);
      return;
    }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.get("/admin/bookings", {
          ...adminHeaders(),
          params: { q: search.trim() },
        });
        setSearchResults(res.data);
      } catch (err) {
        toast.error("Errore ricerca");
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const bookingsToShow = searchResults !== null ? searchResults : bookings;

  useEffect(() => {
    if (getAdminToken()) fetchAll();
    // eslint-disable-next-line
  }, [startDate]);

  const closureMap = useMemo(() => {
    const m = new Map();
    (statusData?.statuses || []).forEach((s) => {
      m.set(`${s.room_id}|${s.date}`, s.is_open);
    });
    return m;
  }, [statusData]);

  const bookingMap = useMemo(() => {
    // map: `${room_id}|${date}` => booking (overnight covers many dates)
    const m = new Map();
    (statusData?.bookings || []).forEach((b) => {
      if (b.booking_type === "overnight") {
        const ci = new Date(b.check_in);
        const co = new Date(b.check_out);
        for (let d = ci; d < co; d = addDays(d, 1)) {
          const key = `${b.room_id}|${format(d, "yyyy-MM-dd")}`;
          const arr = m.get(key) || [];
          arr.push(b);
          m.set(key, arr);
        }
      } else {
        const key = `${b.room_id}|${b.check_in}`;
        const arr = m.get(key) || [];
        arr.push(b);
        m.set(key, arr);
      }
    });
    return m;
  }, [statusData]);

  const toggleClosure = async (room_id, date) => {
    const key = `${room_id}|${date}`;
    const currentOpen = closureMap.has(key) ? closureMap.get(key) : true;
    const newOpen = !currentOpen;
    try {
      await api.put(
        "/admin/room-status",
        { room_id, date, is_open: newOpen },
        adminHeaders(),
      );
      // Update local
      setStatusData((prev) => {
        if (!prev) return prev;
        const rest = prev.statuses.filter(
          (s) => !(s.room_id === room_id && s.date === date),
        );
        return { ...prev, statuses: [...rest, { room_id, date, is_open: newOpen }] };
      });
      toast.success(newOpen ? "Camera aperta" : "Camera chiusa");
    } catch (err) {
      toast.error("Errore aggiornamento");
    }
  };

  const deleteBooking = async (id) => {
    if (!window.confirm("Cancellare questa prenotazione?")) return;
    try {
      await api.delete(`/admin/bookings/${id}`, adminHeaders());
      toast.success("Prenotazione eliminata");
      fetchAll();
    } catch (err) {
      toast.error("Errore eliminazione");
    }
  };

  const logout = () => {
    clearAdminToken();
    navigate("/admin/login");
  };

  const rooms = statusData?.rooms || [];
  const dates = statusData?.dates || [];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-muted/40">
      {/* TopBar */}
      <div className="bg-primary text-primary-foreground sticky top-0 z-40">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="font-display text-xl">Gestionale · Due Pini Conte</div>
          </div>
          <Button
            onClick={logout}
            variant="secondary"
            className="pill"
            data-testid="admin-logout"
          >
            <LogOut className="w-4 h-4 mr-2" /> Esci
          </Button>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard label="Totale prenotazioni" value={stats.total} icon={<CalendarClock className="w-4 h-4" />} />
          <StatCard label="In arrivo" value={stats.upcoming} icon={<BedDouble className="w-4 h-4" />} />
          <StatCard label="DayUse" value={stats.dayuse} icon={<Sun className="w-4 h-4" />} />
          <StatCard label="Pernottamenti" value={stats.overnight} icon={<MoonStar className="w-4 h-4" />} />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setTab("calendar")}
            data-testid="tab-calendar"
            className={`pill px-5 py-2 text-sm ${tab === "calendar" ? "bg-primary text-primary-foreground" : "bg-white border border-border"}`}
          >
            Calendario camere
          </button>
          <button
            onClick={() => setTab("bookings")}
            data-testid="tab-bookings"
            className={`pill px-5 py-2 text-sm ${tab === "bookings" ? "bg-primary text-primary-foreground" : "bg-white border border-border"}`}
          >
            Prenotazioni ({bookings.length})
          </button>
          <button
            onClick={() => setTab("pricing")}
            data-testid="tab-pricing"
            className={`pill px-5 py-2 text-sm ${tab === "pricing" ? "bg-primary text-primary-foreground" : "bg-white border border-border"}`}
          >
            Prezzi
          </button>
          <button
            onClick={() => setTab("revenue")}
            data-testid="tab-revenue"
            className={`pill px-5 py-2 text-sm ${tab === "revenue" ? "bg-primary text-primary-foreground" : "bg-white border border-border"}`}
          >
            Incassi
          </button>
          <button
            onClick={() => setTab("history")}
            data-testid="tab-history"
            className={`pill px-5 py-2 text-sm ${tab === "history" ? "bg-primary text-primary-foreground" : "bg-white border border-border"}`}
          >
            Storico
          </button>
        </div>

        {tab === "calendar" && (
          <div className="bg-white rounded-2xl border border-border overflow-hidden">
            <div className="p-4 flex items-center justify-between border-b border-border">
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => setStartDate(subDays(startDate, DAYS_WINDOW))}
                  variant="ghost"
                  size="sm"
                  data-testid="cal-prev"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <div className="font-display text-lg text-primary">
                  {format(startDate, "d MMM", { locale: it })} — {format(endDate, "d MMM yyyy", { locale: it })}
                </div>
                <Button
                  onClick={() => setStartDate(addDays(startDate, DAYS_WINDOW))}
                  variant="ghost"
                  size="sm"
                  data-testid="cal-next"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <Button
                  onClick={() => setStartDate(new Date(new Date().toDateString()))}
                  variant="ghost"
                  size="sm"
                  data-testid="cal-today"
                >
                  Oggi
                </Button>
              </div>
              <div className="text-xs text-muted-foreground flex items-center gap-4">
                <Legend color="bg-emerald-50 border-emerald-300" label="Aperta" />
                <Legend color="bg-red-50 border-red-300" label="Chiusa" />
                <Legend color="bg-amber-100 border-amber-400" label="Prenotata" />
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-muted/60">
                  <tr>
                    <th className="text-left p-3 sticky left-0 bg-muted/60 z-10 w-56 border-b border-border">
                      Camera
                    </th>
                    {dates.map((d) => {
                      const dt = new Date(d);
                      return (
                        <th
                          key={d}
                          className="text-center p-2 border-b border-border font-normal min-w-[70px]"
                        >
                          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                            {format(dt, "EEE", { locale: it })}
                          </div>
                          <div className="text-primary font-display text-lg">
                            {format(dt, "d")}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {rooms.map((r) => (
                    <tr key={r.id} className="border-b border-border/60">
                      <td className="p-3 sticky left-0 bg-white z-10 border-r border-border">
                        <div className="font-medium text-primary">{r.name}</div>
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                          {PROPERTY_NAMES[r.property]}
                        </div>
                      </td>
                      {dates.map((d) => {
                        const key = `${r.id}|${d}`;
                        const isOpen = closureMap.has(key) ? closureMap.get(key) : true;
                        const dayBookings = bookingMap.get(key) || [];
                        const hasBooking = dayBookings.length > 0;
                        let bg = "bg-emerald-50 hover:bg-emerald-100 border-emerald-300";
                        let label = "APERTA";
                        if (hasBooking) {
                          bg = "bg-amber-100 border-amber-400 hover:bg-amber-200";
                          label = dayBookings[0].booking_type === "dayuse" ? "DAYUSE" : "NOTTE";
                        } else if (!isOpen) {
                          bg = "bg-red-50 border-red-300 hover:bg-red-100";
                          label = "CHIUSA";
                        }
                        return (
                          <td key={d} className="p-1 text-center">
                            <button
                              onClick={() => !hasBooking && toggleClosure(r.id, d)}
                              disabled={hasBooking}
                              data-testid={`cell-${r.id}-${d}`}
                              title={
                                hasBooking
                                  ? `${dayBookings[0].guest_first_name} ${dayBookings[0].guest_last_name}`
                                  : isOpen
                                    ? "Chiudi camera"
                                    : "Apri camera"
                              }
                              className={`w-full min-w-[60px] py-2 rounded-md border text-[10px] font-semibold tracking-wider ${bg} ${hasBooking ? "cursor-not-allowed" : "cursor-pointer"}`}
                            >
                              {label}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "bookings" && (
          <div className="bg-white rounded-2xl border border-border overflow-hidden">
            <div className="p-4 flex items-center justify-between border-b border-border gap-4 flex-wrap">
              <div className="font-display text-lg text-primary shrink-0">
                {searchResults !== null
                  ? `Ricerca · ${bookingsToShow.length} risultat${bookingsToShow.length === 1 ? "o" : "i"}`
                  : `Prenotazioni in arrivo · ${bookings.length}`}
              </div>
              <div className="flex items-center gap-2 flex-1 max-w-md">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    data-testid="booking-search"
                    placeholder="Cerca DPC-2026-... nome, cognome, telefono"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9 pr-9"
                  />
                  {search && (
                    <button
                      onClick={() => setSearch("")}
                      data-testid="clear-search"
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted"
                    >
                      <X className="w-4 h-4 text-muted-foreground" />
                    </button>
                  )}
                </div>
                {searching && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
              </div>
              <Button
                onClick={exportCSV}
                data-testid="export-csv"
                className="btn-primary pill"
                size="sm"
              >
                <Download className="w-4 h-4 mr-2" />
                Esporta CSV
              </Button>
            </div>
            <table className="min-w-full text-sm">
              <thead className="bg-muted/60">
                <tr className="text-left">
                  <th className="p-3">Riferimento</th>
                  <th className="p-3">Tipo</th>
                  <th className="p-3">Camera</th>
                  <th className="p-3">Ospite</th>
                  <th className="p-3">Data</th>
                  <th className="p-3">Contatto</th>
                  <th className="p-3">€</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {bookingsToShow.map((b) => (
                  <tr key={b.id} className="border-t border-border/60" data-testid={`booking-row-${b.id}`}>
                    <td className="p-3">
                      <span className="font-mono text-xs bg-muted px-2 py-1 rounded">
                        {b.reference || "—"}
                      </span>
                    </td>
                    <td className="p-3">
                      <span
                        className={`inline-flex items-center gap-1 pill px-2 py-1 text-[11px] font-semibold ${
                          b.booking_type === "dayuse"
                            ? "bg-amber-100 text-amber-800"
                            : "bg-primary/10 text-primary"
                        }`}
                      >
                        {b.booking_type === "dayuse" ? <Sun className="w-3 h-3" /> : <MoonStar className="w-3 h-3" />}
                        {b.booking_type === "dayuse" ? "DayUse" : "Notte"}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="font-medium">{b.room_name}</div>
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                        {PROPERTY_NAMES[b.property]}
                      </div>
                    </td>
                    <td className="p-3">
                      {b.guest_first_name} {b.guest_last_name}
                      {b.guests > 1 && (
                        <div className="text-[10px] text-muted-foreground">
                          {b.guests} ospiti
                        </div>
                      )}
                    </td>
                    <td className="p-3">
                      {b.booking_type === "dayuse" ? (
                        <>
                          {format(new Date(b.check_in), "d MMM yyyy", { locale: it })}
                          <div className="text-xs text-muted-foreground">
                            {b.slot_start} – {b.slot_end}
                          </div>
                        </>
                      ) : (
                        <>
                          {format(new Date(b.check_in), "d MMM", { locale: it })} →{" "}
                          {format(new Date(b.check_out), "d MMM yyyy", { locale: it })}
                        </>
                      )}
                    </td>
                    <td className="p-3">
                      <div>{b.guest_phone}</div>
                      {b.guest_email && (
                        <div className="text-xs text-muted-foreground">{b.guest_email}</div>
                      )}
                    </td>
                    <td className="p-3 font-medium">€{b.price}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-1">
                        <Button
                          onClick={() => setQrBooking(b)}
                          variant="ghost"
                          size="sm"
                          title="Mostra QR check-in"
                          data-testid={`qr-booking-${b.id}`}
                        >
                          <QrCode className="w-4 h-4 text-primary" />
                        </Button>
                        <Button
                          onClick={() => deleteBooking(b.id)}
                          variant="ghost"
                          size="sm"
                          data-testid={`delete-booking-${b.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {bookingsToShow.length === 0 && (
                  <tr>
                    <td colSpan={8} className="p-10 text-center text-muted-foreground">
                      {searchResults !== null
                        ? "Nessun risultato per questa ricerca."
                        : "Nessuna prenotazione in arrivo."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === "pricing" && pricingDraft && (
          <div className="bg-white rounded-2xl border border-border p-8 max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <Tag className="w-5 h-5 text-primary" />
              <div>
                <div className="font-display text-2xl text-primary">Tariffe</div>
                <div className="text-sm text-muted-foreground">
                  Modifica i prezzi delle prenotazioni. Le modifiche saranno subito attive sul sito.
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <PriceField
                label="DayUse — camera standard (3h)"
                value={pricingDraft.dayuse_standard_3h}
                onChange={(v) => setPricingDraft({ ...pricingDraft, dayuse_standard_3h: v })}
                testid="price-dayuse-standard"
              />
              <PriceField
                label="DayUse — Suite con vasca (2h)"
                value={pricingDraft.dayuse_vasca_2h}
                onChange={(v) => setPricingDraft({ ...pricingDraft, dayuse_vasca_2h: v })}
                testid="price-dayuse-vasca"
              />
              <PriceField
                label="Pernottamento — camera standard"
                value={pricingDraft.overnight_standard}
                onChange={(v) => setPricingDraft({ ...pricingDraft, overnight_standard: v })}
                testid="price-overnight-standard"
              />
              <PriceField
                label="Pernottamento — Suite con vasca"
                value={pricingDraft.overnight_suite}
                onChange={(v) => setPricingDraft({ ...pricingDraft, overnight_suite: v })}
                testid="price-overnight-suite"
              />
              <PriceField
                label="Pernottamento — camera B&B G&A"
                value={pricingDraft.overnight_bnb}
                onChange={(v) => setPricingDraft({ ...pricingDraft, overnight_bnb: v })}
                testid="price-overnight-bnb"
              />
              <PriceField
                label="Supplemento per ospite extra (oltre i 2)"
                value={pricingDraft.supplement_per_extra_guest}
                onChange={(v) => setPricingDraft({ ...pricingDraft, supplement_per_extra_guest: v })}
                testid="price-supplement"
              />
              <PriceField
                label="Supplemento weekend (Ven-Sab, per notte)"
                value={pricingDraft.weekend_surcharge_per_night}
                onChange={(v) => setPricingDraft({ ...pricingDraft, weekend_surcharge_per_night: v })}
                testid="price-weekend"
              />
            </div>

            <div className="mt-8 flex items-center gap-3">
              <Button
                onClick={savePricing}
                disabled={savingPricing}
                className="btn-primary pill"
                data-testid="save-pricing"
              >
                {savingPricing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Salva prezzi
              </Button>
              <Button
                onClick={() => setPricingDraft(pricing)}
                variant="ghost"
                data-testid="reset-pricing"
              >
                Annulla modifiche
              </Button>
            </div>
          </div>
        )}
        {tab === "revenue" && <RevenueTab key={bookings.length} />}
        {tab === "history" && <HistoryTab />}
      </div>

      <BookingSuccessDialog
        open={!!qrBooking}
        booking={qrBooking}
        onClose={() => setQrBooking(null)}
      />
    </div>
  );
}

const PriceField = ({ label, value, onChange, testid }) => (
  <div>
    <Label className="text-xs uppercase tracking-widest text-muted-foreground">
      {label}
    </Label>
    <div className="mt-2 relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">€</span>
      <Input
        type="number"
        step="0.01"
        min="0"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="pl-7 font-display text-lg"
        data-testid={testid}
      />
    </div>
  </div>
);

const StatCard = ({ label, value, icon }) => (
  <div className="bg-white rounded-2xl border border-border p-5">
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs uppercase tracking-widest text-muted-foreground">{label}</span>
      <span className="text-primary">{icon}</span>
    </div>
    <div className="font-display text-3xl text-primary">{value}</div>
  </div>
);

const Legend = ({ color, label }) => (
  <div className="flex items-center gap-2">
    <span className={`w-4 h-4 rounded border ${color}`} />
    {label}
  </div>
);
